import { z } from "zod";
import { Form } from "src/components/common/form";
import { Input } from "src/components/common/form/input";
import { Select } from "src/components/common/form/select";
import { usePostBusScheduleMutation } from "src/services/react-query-apis/post-bus-schedule";
import { usePutBusScheduleMutation } from "src/services/react-query-apis/put-bus-schedule";
import { toast } from "react-toastify";
import { useBusCoursesQuery } from "src/services/react-query-apis/get-bus-courses";
import { useBusesQuery } from "src/services/react-query-apis/get-buses";
import { useFormContext } from "react-hook-form";
import { useEffect } from "react";
import Loader from "src/components/common/Loader/Loader";

const scheduleEditInputSchema = z.object({
  day_week: z
    .string()
    .min(1, { message: "曜日を選択してください。" })
    .refine(val => ["月", "火", "水", "木", "金", "土", "日"].includes(val), {
      message: "無効な曜日が選択されました。",
    }),
  outbound_departure_time: z
    .string()
    .regex(/^[0-9]{2}:[0-9]{2}:[0-9]{2}$/, {
      message: "出発時刻は時:分:秒の形式で入力してください。",
    })
    .min(1, { message: "出発時刻を入力してください。" }),
  return_departure_time: z
    .string()
    .regex(/^[0-9]{2}:[0-9]{2}:[0-9]{2}$/, {
      message: "出発時刻は時:分:秒の形式で入力してください。",
    })
    .min(1, { message: "出発時刻を入力してください。" }),
  bus_course_id: z.coerce
    .number()
    .min(1, { message: "バスコースを選択してください。" }),
  outbound_bus_id: z.coerce
    .number()
    .min(1, { message: "バスを選択してください。" }),
  return_bus_id: z.coerce
    .number()
    .min(1, { message: "バスを選択してください。" }),
  description: z.string().min(1, { message: "説明を入力してください。" }),
});

const dayOptions = [
  { value: "", label: "曜日を選択" },
  { value: "月", label: "月曜日" },
  { value: "火", label: "火曜日" },
  { value: "水", label: "水曜日" },
  { value: "木", label: "木曜日" },
  { value: "金", label: "金曜日" },
  { value: "土", label: "土曜日" },
  { value: "日", label: "日曜日" },
];

const ScheduleEditForm = ({
  selectedSchedule,
  selectedMonth,
  onSubmit,
  isSubmitting,
  onClose,
}) => {
  const busCoursesQuery = useBusCoursesQuery({});
  const busesQuery = useBusesQuery({ month: selectedMonth });

  const courseOptions = [
    { value: "", label: "コースを選択" },
    ...(busCoursesQuery.data?.data.map(course => ({
      value: course.id,
      label: `${course.code} - ${course.description}`,
    })) || []),
  ];

  const busOptions = [
    { value: "", label: "バスを選択" },
    ...(busesQuery.data?.data.map(bus => ({
      value: bus.id,
      label: `${bus.name} (${bus.capacity}人)`,
    })) || []),
  ];

  if (busesQuery.isPending || busCoursesQuery.isPending) {
    return (
      <div className="d-flex justify-content-center pb-5">
        <Loader />
      </div>
    );
  }

  return (
    <Form
      onSubmit={onSubmit}
      options={{
        defaultValues: {
          day_week: selectedSchedule?.day_week || "",
          outbound_departure_time:
            selectedSchedule?.outbound_departure_time || "",
          return_departure_time: selectedSchedule?.return_departure_time || "",
          bus_course_id: selectedSchedule?.bus_course_id || "",
          outbound_bus_id: selectedSchedule?.outbound_bus_id || "",
          return_bus_id: selectedSchedule?.return_bus_id || "",
          description: selectedSchedule?.description || "",
          outbound_bus_schedule_pair_id:
            selectedSchedule?.outbound_bus_schedule_pair_id || "",
          return_bus_schedule_pair_id:
            selectedSchedule?.return_bus_schedule_pair_id || "",
        },
      }}
      schema={scheduleEditInputSchema}
    >
      {({ register, formState }) => {
        return (
          <>
            <div className="row">
              <div className="col-4">
                <Select
                  label="曜日"
                  options={dayOptions}
                  error={formState.errors["day_week"]}
                  registration={register("day_week")}
                />
              </div>
              <div className="col-4">
                <Input
                  type="text"
                  label="往路出発時間"
                  name="outbound_departure_time"
                  error={formState.errors["outbound_departure_time"]}
                  registration={register("outbound_departure_time")}
                  placeholder="HH:MM:SS"
                />
              </div>
              <div className="col-4">
                <Input
                  type="text"
                  label="復路出発時間"
                  name="return_departure_time"
                  error={formState.errors["return_departure_time"]}
                  registration={register("return_departure_time")}
                  placeholder="HH:MM:SS"
                />
              </div>
            </div>
            <div className="row">
              <div className="col-4">
                <Select
                  label="往路バス"
                  options={busOptions}
                  error={formState.errors["outbound_bus_id"]}
                  registration={register("outbound_bus_id")}
                />
              </div>
              <div className="col-4">
                <Select
                  label="復路バス"
                  options={busOptions}
                  error={formState.errors["return_bus_id"]}
                  registration={register("return_bus_id")}
                />
              </div>
              <div className="col-4">
                <Select
                  label="バスコース"
                  options={courseOptions}
                  error={formState.errors["bus_course_id"]}
                  registration={register("bus_course_id")}
                  readOnly={!!selectedSchedule}
                />
              </div>
              <div className="col-4">
                <DescriptionInput selectedMonth={selectedMonth} />
              </div>
            </div>
            <div className="d-flex gap-2 justify-content-end">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
              >
                閉じる
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? "処理中..."
                  : selectedSchedule
                    ? "確認"
                    : "追加"}
              </button>
            </div>
          </>
        );
      }}
    </Form>
  );
};

const DescriptionInput = ({ selectedMonth }) => {
  const { register, formState, watch, setValue } = useFormContext();

  const dayWeek = watch("day_week");
  const outboundDepartureTime = watch("outbound_departure_time");
  const returnDepartureTime = watch("return_departure_time");

  const busCourseId = watch("bus_course_id");
  const busCoursesQuery = useBusCoursesQuery({});
  const busCourse = busCoursesQuery.data?.data.find(
    course => course.id === Number(busCourseId)
  );
  const outboundBusId = watch("outbound_bus_id");
  const returnBusId = watch("return_bus_id");
  const busesQuery = useBusesQuery({ month: selectedMonth });
  const outboundBus = busesQuery.data?.data.find(
    bus => bus.id === Number(outboundBusId)
  );
  const returnBus = busesQuery.data?.data.find(
    bus => bus.id === Number(returnBusId)
  );

  useEffect(() => {
    if (
      dayWeek &&
      outboundDepartureTime &&
      returnDepartureTime &&
      busCourse &&
      outboundBus &&
      returnBus
    ) {
      const description = `A－${
        ((Number(outboundDepartureTime.split(":")[0]) +
          Number(returnDepartureTime.split(":")[0])) /
          2) %
        12
      } ${dayWeek}曜日 ${busCourse.code} コース ${outboundBus.name} ${returnBus.name}`;

      setValue("description", description);
    }
  }, [
    dayWeek,
    outboundDepartureTime,
    returnDepartureTime,
    busCourse,
    outboundBus,
    returnBus,
    setValue,
  ]);

  return (
    <Input
      type="text"
      label="説明"
      name="description"
      error={formState.errors["description"]}
      registration={register("description")}
      placeholder="説明"
    />
  );
};

export default ScheduleEditForm;
